import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, Paper, Alert, AlertTitle, Button, TextField, Checkbox,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, Tooltip,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import { BRAND } from "../../styles/theme";
import {
  buscarRecupero, reinyectarRecupero, fmtMonto,
  type RecuperoFaltante, type RecuperoBuscarResult, type RecuperoReinyeccion,
} from "../../services/cmf";

const MONO = '"SF Mono","JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
const thSx = {
  fontWeight: 700, fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase",
  color: BRAND.primaryDark, borderBottom: "none", py: 1.2,
} as const;

// El backend trabaja en hora Argentina (los movimientos CMF vienen en ART). El
// operador puede estar en otro huso: se calcula "ahora" explícitamente en ART.
function nowART(): Date {
  const s = new Date().toLocaleString("en-US", { timeZone: "America/Argentina/Buenos_Aires" });
  return new Date(s);
}
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function defaultRange(): { from: string; to: string } {
  const now = nowART();
  const from = new Date(now.getTime() - 60 * 60 * 1000);
  // Si la última hora cruza medianoche, la búsqueda es sólo de hoy: arranca en 00:00.
  if (from.getDate() !== now.getDate()) return { from: "00:00", to: hhmm(now) };
  return { from: hhmm(from), to: hhmm(now) };
}
function fmtHora(iso: string): string {
  return iso ? iso.slice(11, 19) : "—";
}

type Progreso = { pagina: number; total: number | null; leidos: number; creditos: number; registrados: number };

export default function CmfRecupero() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [buscando, setBuscando] = useState(false);
  const [progreso, setProgreso] = useState<Progreso | null>(null);
  const [faltantes, setFaltantes] = useState<RecuperoFaltante[] | null>(null);
  const [resumen, setResumen] = useState<Pick<RecuperoBuscarResult, "day" | "from" | "to"> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<RecuperoFaltante[] | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState<RecuperoReinyeccion[] | null>(null);
  const cancel = useRef(false);

  const buscar = useCallback(async () => {
    setErr(null); setResultados(null); setSel(new Set());
    setFaltantes(null); setResumen(null);
    setBuscando(true); cancel.current = false;
    const acc: RecuperoFaltante[] = [];
    let page: number | null = 1;
    let leidos = 0, creditos = 0, registrados = 0;
    try {
      while (page !== null && !cancel.current) {
        const r: RecuperoBuscarResult = await buscarRecupero({ from, to, page });
        leidos += r.movimientos_leidos; creditos += r.creditos_en_ventana; registrados += r.registrados;
        acc.push(...r.faltantes);
        setProgreso({ pagina: r.page_to, total: r.total_paginas, leidos, creditos, registrados });
        setFaltantes([...acc].sort((a, b) => b.fecha_hora.localeCompare(a.fecha_hora)));
        setResumen({ day: r.day, from: r.from, to: r.to });
        page = r.next_page;
      }
    } catch (e: any) {
      setErr(e?.message ?? "Error al buscar");
    } finally {
      setBuscando(false);
    }
  }, [from, to]);

  const reinyectables = useMemo(() => (faltantes ?? []).filter((f) => f.reinyectable), [faltantes]);
  const allSelected = reinyectables.length > 0 && reinyectables.every((f) => sel.has(f.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(reinyectables.map((f) => f.id)));
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const pedirConfirmacion = (items: RecuperoFaltante[]) => setConfirm(items);

  const reinyectar = async () => {
    if (!confirm) return;
    setEnviando(true); setErr(null);
    try {
      const r = await reinyectarRecupero(confirm.map((f) => ({ id: f.id, cuit_pagador: f.cuit_pagador })));
      setResultados(r.resultados);
      setConfirm(null);
      // Se vuelve a correr el cruce: lo reinyectado tiene que desaparecer de la lista.
      await buscar();
    } catch (e: any) {
      setErr(e?.message ?? "Error al reinyectar");
    } finally {
      setEnviando(false);
    }
  };

  const totalSel = reinyectables.filter((f) => sel.has(f.id)).reduce((a, f) => a + f.monto, 0);
  const totalConfirm = (confirm ?? []).reduce((a, f) => a + f.monto, 0);

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Recupero de webhooks Coelsa</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Busca cobros que el banco acreditó pero que Cucuru no registró porque el aviso (webhook) de
        Coelsa no llegó, típicamente durante un corte de conexión con Coelsa. Cruza los créditos por
        DEBIN de la cuenta recaudadora en CMF contra lo registrado en el core, dentro de un rango
        horario del día de hoy. Lo que falta se puede reinyectar: se publica en la cola del core el
        mismo mensaje que hubiera mandado el webhook, y el core lo procesa como un cobro normal.
        Reinyectar algo ya registrado es inocuo: el core deduplica por id.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Hoy (hora Argentina)</Typography>
          <TextField label="Desde" type="time" size="small" value={from}
                     onChange={(e) => setRange({ from: e.target.value, to })}
                     slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 140 }} />
          <TextField label="Hasta" type="time" size="small" value={to}
                     onChange={(e) => setRange({ from, to: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 140 }} />
          <Button variant="outlined" size="small" onClick={() => setRange(defaultRange())} disabled={buscando}>
            Última hora
          </Button>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={buscar}
                  disabled={buscando || !from || !to || from >= to}>
            Buscar diferencias
          </Button>
          {buscando && (
            <Button size="small" color="inherit" onClick={() => { cancel.current = true; }}>Detener</Button>
          )}
        </Stack>
        {from >= to && (
          <Typography variant="caption" color="error">"Desde" tiene que ser anterior a "Hasta".</Typography>
        )}
        {(buscando || progreso) && (
          <Box sx={{ mt: 1.5 }}>
            {buscando && <LinearProgress sx={{ mb: 0.5 }} />}
            {progreso && (
              <Typography variant="caption" color="text.secondary">
                Páginas leídas de CMF: {progreso.pagina}{progreso.total ? ` de ${progreso.total}` : ""} ·
                movimientos: {progreso.leidos} · créditos DEBIN en el rango: {progreso.creditos} ·
                registrados en el core: {progreso.registrados}
                {buscando && " · buscando…"}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {resultados && (
        <Alert severity={resultados.every((r) => r.ok) ? "success" : "warning"} sx={{ mb: 2 }}>
          <AlertTitle>Reinyección: {resultados.filter((r) => r.resultado === "enviado").length} enviada(s)</AlertTitle>
          {resultados.map((r) => (
            <div key={r.id}>
              <span style={{ fontFamily: MONO }}>{r.id}</span> — {r.resultado}
              {r.detalle ? `: ${r.detalle}` : ""}{r.message_id ? ` (MessageId ${r.message_id})` : ""}
            </div>
          ))}
        </Alert>
      )}

      {faltantes !== null && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Sin registrar en Cucuru: {faltantes.length}
            </Typography>
            {resumen && (
              <Typography variant="caption" color="text.secondary">
                {resumen.day} · {resumen.from}–{resumen.to} (ART)
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {reinyectables.length > 0 && (
              <Button variant="contained" color="warning" startIcon={<ReplayIcon />}
                      disabled={sel.size === 0 || buscando || enviando}
                      onClick={() => pedirConfirmacion(reinyectables.filter((f) => sel.has(f.id)))}>
                Reinyectar seleccionados ({sel.size}) · ${fmtMonto(totalSel)}
              </Button>
            )}
          </Stack>

          {faltantes.length === 0 && !buscando && (
            <Alert severity="success">Todos los créditos por DEBIN del rango están registrados en el core.</Alert>
          )}

          {faltantes.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected} onChange={toggleAll}
                                indeterminate={sel.size > 0 && !allSelected} disabled={reinyectables.length === 0} />
                    </TableCell>
                    <TableCell sx={thSx}>Hora CMF</TableCell>
                    <TableCell sx={thSx}>ID Coelsa</TableCell>
                    <TableCell sx={thSx} align="right">Monto</TableCell>
                    <TableCell sx={thSx}>Colector (CVU destino)</TableCell>
                    <TableCell sx={thSx}>Pagador</TableCell>
                    <TableCell sx={thSx}>Estado CMF</TableCell>
                    <TableCell sx={thSx}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faltantes.map((f) => (
                    <TableRow key={f.id} hover selected={sel.has(f.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={sel.has(f.id)} onChange={() => toggle(f.id)} disabled={!f.reinyectable} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: MONO }}>{fmtHora(f.fecha_hora)}</TableCell>
                      <TableCell sx={{ fontFamily: MONO, fontSize: 12 }}>{f.id}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO, fontWeight: 700 }}>{fmtMonto(f.monto)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.titular || "—"}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: MONO, color: "text.secondary" }}>
                          {f.cvu || "—"}{f.cuit_destino ? ` · CUIT ${f.cuit_destino}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: MONO, color: "text.secondary" }}>
                          {f.cuit_pagador || "—"}<br />{f.cuenta_origen || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={f.motivo ?? ""} arrow>
                          <Chip size="small" label={f.estado_cmf || "?"}
                                color={f.reinyectable ? "success" : "default"} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />}
                                disabled={!f.reinyectable || buscando || enviando}
                                onClick={() => pedirConfirmacion([f])}>
                          Reinyectar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={!!confirm} onClose={() => !enviando && setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reinyectar {confirm?.length === 1 ? "1 cobro" : `${confirm?.length ?? 0} cobros`} en el core</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Se va a publicar en la cola del core un aviso por cada cobro, igual al que hubiera mandado
            Coelsa. El core lo acredita al colector y dispara los avisos de cobro configurados. Total:
            <strong> ${fmtMonto(totalConfirm)}</strong>.
          </DialogContentText>
          {(confirm ?? []).map((f) => (
            <Typography key={f.id} variant="body2" sx={{ fontFamily: MONO, fontSize: 12 }}>
              {f.id} · ${fmtMonto(f.monto)} → {f.titular} ({f.cvu})
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={enviando}>Cancelar</Button>
          <Button onClick={reinyectar} variant="contained" color="warning" disabled={enviando}
                  startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}>
            {enviando ? "Enviando…" : "Confirmar reinyección"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
